import type { JSX } from 'react';

/**
 * The icon set, one name per glyph Android draws.
 *
 * Named after the Material icon rather than after the thing it happens to mean
 * on one screen (`'cloud-off'`, not `'waiting'`), so a screen that reuses a
 * glyph does not end up asking for a name that lies about what appears.
 *
 * `'phone-iphone'` is the single substitution: Android shows
 * `Icons.Outlined.PhoneAndroid` next to the device model, and a phone drawn with
 * Android's silhouette on an iPhone would be a small, permanent lie about which
 * app produced the row.
 */
export type IconName =
  // bottom nav — Icons.Outlined.Speed / FactCheck / Settings
  | 'speed'
  | 'fact-check'
  | 'settings'
  | 'language'
  | 'chevron-right'
  | 'key-off'
  | 'play-arrow'
  | 'stop'
  | 'schedule'
  | 'cloud-off'
  | 'error-outline'
  | 'wifi'
  | 'network-cell'
  | 'signal-wifi-off'
  | 'warning'
  // replaces Icons.Outlined.PhoneAndroid
  | 'phone-iphone'
  | 'content-copy'
  | 'check-circle'
  | 'cancel'
  | 'info';

/**
 * Path data copied verbatim from Google's own 24 px sources
 * (`google/material-design-icons`), in the same variant Compose resolves for the
 * call site on Android: the **outlined** asset for everything Android asks for
 * as `Icons.Outlined.*`, and the **filled** one for `wifi`, `warning`, `stop`,
 * `signal-wifi-off`, `network-cell` and `check-circle`, which Android draws as
 * `Icons.Filled.*`. Hand-drawn approximations were not an option: the two apps
 * are photographed side by side in the field, and a glyph that is nearly the
 * same icon reads as a different app.
 *
 * Inlined as data rather than loaded as an icon font because a field device may
 * be on a slow or filtered SIM. A webfont that fails to arrive leaves either
 * blank boxes or tofu where the navigation used to be; a path in the bundle
 * cannot fail separately from the app itself.
 *
 * Several glyphs are more than one subpath. They must all be rendered — dropping
 * the second path of `speed` leaves the dial without its needle.
 */
const GLYPHS: Record<IconName, readonly string[]> = {
  speed: [
    'M20.38 8.57l-1.23 1.85a8 8 0 0 1-.22 7.58H5.07A8 8 0 0 1 15.58 6.85l1.85-1.23A10 10 0 0 0 3.35 19a2 2 0 0 0 1.72 1h13.85a2 2 0 0 0 1.74-1 10 10 0 0 0-.27-10.44z',
    'M10.59 15.41a2 2 0 0 0 2.83 0l5.66-8.49-8.49 5.66a2 2 0 0 0 0 2.83z',
  ],
  'fact-check': [
    'M20,3H4C2.9,3,2,3.9,2,5v14c0,1.1,0.9,2,2,2h16c1.1,0,2-0.9,2-2V5 C22,3.9,21.1,3,20,3z M20,19H4V5h16V19z',
    'M19.41,10.42 17.99,9 14.82,12.17 13.41,10.75 12,12.16 14.82,15z',
    'M5,7h5v2H5z',
    'M5,11h5v2H5z',
    'M5,15h5v2H5z',
  ],
  settings: ['M19.43 12.98c.04-.32.07-.64.07-.98 0-.34-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.09-.16-.26-.25-.44-.25-.06 0-.12.01-.17.03l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.06-.02-.12-.03-.18-.03-.17 0-.34.09-.43.25l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98 0 .33.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.09.16.26.25.44.25.06 0 .12-.01.17-.03l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.06.02.12.03.18.03.17 0 .34-.09.43-.25l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zm-1.98-1.71c.04.31.05.52.05.73 0 .21-.02.43-.05.73l-.14 1.13.89.7 1.08.84-.7 1.21-1.27-.51-1.04-.42-.9.68c-.43.32-.84.56-1.25.73l-1.06.43-.16 1.13-.2 1.35h-1.4l-.19-1.35-.16-1.13-1.06-.43c-.43-.18-.83-.41-1.23-.71l-.91-.7-1.06.43-1.27.51-.7-1.21 1.08-.84.89-.7-.14-1.13c-.03-.31-.05-.54-.05-.74s.02-.43.05-.73l.14-1.13-.89-.7-1.08-.84.7-1.21 1.27.51 1.04.42.9-.68c.43-.32.84-.56 1.25-.73l1.06-.43.16-1.13.2-1.35h1.39l.19 1.35.16 1.13 1.06.43c.43.18.83.41 1.23.71l.91.7 1.06-.43 1.27-.51.7 1.21-1.07.85-.89.7.14 1.13zM12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z'],
  language: ['M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2s.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2s.07-1.35.16-2h4.68c.09.65.16 1.32.16 2s-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2s-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z'],
  'chevron-right': ['M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6-6-6z'],
  'key-off': ['M10.7,13.53l-1.71-1.71C9,11.88,9,11.94,9,12c0,1.1-0.9,2-2,2s-2-0.9-2-2s0.9-2,2-2c0.06,0,0.12,0,0.18,0.01L5.47,8.3 C4.02,8.9,3,10.33,3,12c0,2.21,1.79,4,4,4C8.67,16,10.1,14.98,10.7,13.53z M12.19,15.02C11.15,16.8,9.21,18,7,18 c-3.31,0-6-2.69-6-6c0-2.21,1.2-4.15,2.98-5.19L1.39,4.22l1.41-1.41l18.38,18.38l-1.41,1.41L12.19,15.02z M16.26,13.43l1.24-0.93 l1.81,1.36L21.17,12l-1-1l-6.34,0l-2-2L21,9l0,0l3,3l-4.5,4.5l-0.69-0.51L16.26,13.43z'],
  'play-arrow': ['M10 8.64L15.27 12 10 15.36V8.64M8 5v14l11-7L8 5z'],
  stop: ['M6 6h12v12H6z'],
  schedule: ['M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z'],
  'cloud-off': ['M24 15c0-2.64-2.05-4.78-4.65-4.96C18.67 6.59 15.64 4 12 4c-1.33 0-2.57.36-3.65.97l1.49 1.49C10.51 6.17 11.23 6 12 6c3.04 0 5.5 2.46 5.5 5.5v.5H19c1.66 0 3 1.34 3 3 0 .99-.48 1.85-1.21 2.4l1.41 1.41c1.09-.92 1.8-2.27 1.8-3.81zM4.41 3.86L3 5.27l2.77 2.77h-.42C2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h11.73l2 2 1.41-1.41L4.41 3.86zM6 18c-2.21 0-4-1.79-4-4s1.79-4 4-4h1.73l8 8H6z'],
  'error-outline': ['M11 15h2v2h-2v-2zm0-8h2v6h-2V7zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z'],
  wifi: ['M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z'],
  'network-cell': ['M2,22h20V2L2,22z M20,20h-3V9.83l3-3V20z'],
  'signal-wifi-off': ['M23.64 7c-.45-.34-4.93-4-11.64-4-1.5 0-2.89.19-4.15.48L18.18 13.8 23.64 7zm-6.6 8.22L3.27 1.44 2 2.72l2.05 2.06C1.91 5.76.59 6.82.36 7l11.63 14.49.01.01.01-.01 3.9-4.86 3.32 3.32 1.27-1.27-3.46-3.46z'],
  warning: ['M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z'],
  'phone-iphone': ['M15.5 1h-8C6.12 1 5 2.12 5 3.5v17C5 21.88 6.12 23 7.5 23h8c1.38 0 2.5-1.12 2.5-2.5v-17C18 2.12 16.88 1 15.5 1zm-4 21c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4.5-4H7V4h9v14z'],
  'content-copy': ['M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z'],
  'check-circle': ['M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z'],
  cancel: ['M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.59-13L12 10.59 8.41 7 7 8.41 10.59 12 7 15.59 8.41 17 12 13.41 15.59 17 17 15.59 13.41 12 17 8.41z'],
  info: ['M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z'],
};

/**
 * One Material glyph, sized in px and coloured by the surrounding text unless
 * told otherwise.
 *
 * Default size is 20 px — Android's `Spacing.iconRow`, the size used in list
 * rows, which is where most of these appear. `--icon-inline` (18) and
 * `--icon-prominent` (24) are passed explicitly by the few callers that need
 * them, exactly as the Android composables do.
 *
 * `fill="currentColor"` means an icon inherits the colour of the text it sits
 * next to, so a row that changes state tints its icon and its label together
 * with one CSS rule. An icon that is deliberately a *different* colour from its
 * label (a `--warn` bullet in front of `--text-primary` text) gets a `.c-*`
 * utility in `className`, which sets `color` and is therefore what
 * `currentColor` resolves to. There is no `color` prop: it took a token string
 * (`var(--alive)`) and wrote it into the inline `style` — because `var()` only
 * resolves in a CSS property value, never in the SVG `fill` presentation
 * attribute — which is a second spelling of the palette in a prop, one the
 * stylesheet cannot see.
 *
 * Decorative by default: an icon beside a label repeats what the label already
 * says, and a screen reader that announces both reads every row twice. Pass
 * `title` only when the icon is the entire control (an icon-only button), which
 * switches it to `role="img"` with an accessible name.
 */
export function Icon({
  name,
  size = 20,
  className,
  title,
}: {
  name: IconName;
  size?: number;
  className?: string;
  title?: string;
}): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      className={className}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
    >
      {title ? <title>{title}</title> : null}
      {/*
        Keyed by index, which is safe here and nowhere near the usual list-key
        trap: `GLYPHS` is a module constant, so a glyph's subpaths are a fixed
        array that is never reordered, filtered or appended to at runtime. The
        alternative is the path itself, and `settings` is a ~1200-character
        string that React would then string-compare on every reconciliation.
      */}
      {GLYPHS[name].map((d, index) => (
        <path key={index} d={d} />
      ))}
    </svg>
  );
}
